import React, { useState } from 'react';
import SEO from '../SEO';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Globe, ShieldCheck, ChevronDown, ChevronUp, Check, Clock, BarChart3, Zap, Target, TrendingUp, Search, Activity, Users } from 'lucide-react';
import { getBrand } from '../../utils/brand';
import { PRICING_MATRIX, formatVolume, normalizePublicPricingCopy, TierId } from '../../constants/pricing';
import { shouldUseRedesignForSurface, useUiReleaseMode } from '../../config/uiRelease';
import { PUBLIC_LANDING_PATHS } from '../../routes/publicLandingPaths';
import LazySection from './LazySection';
import TrafficLandingV2 from './TrafficLandingV2';
import {
    materializeLandingLocalization,
    useLandingLocalizationArtifact,
} from '../../utils/landingLocalization';

const LazyBlockOne = React.lazy(() => import('./TrafficLandingLazyContent').then(m => ({ default: m.ContentBlockOne })));
const LazyBlockTwo = React.lazy(() => import('./TrafficLandingLazyContent').then(m => ({ default: m.ContentBlockTwo })));

export interface SeoPricingPackage {
    credits: number;
    free: number;
    price: number;
    originalPrice: number;
    discountLabel: string;
}

export interface SeoPricingConfig {
    type: 'gsc' | 'local';
    packages: SeoPricingPackage[];
    h2?: string;
    subtitle?: string;
    unitLabel?: string;
}

export interface ResellerPricingTier {
    name: string;
    monthlyPrice: string;
    setupFee: string;
    discount: string;
    subAccounts: string;
    features: string[];
    highlighted?: boolean;
    ctaLabel?: string;
    ctaHref?: string;
}

export interface ResellerPricingConfig {
    h2?: string;
    subtitle?: string;
    tiers: ResellerPricingTier[];
}

export interface LandingFeature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

export interface LandingTestimonial {
    name: string;
    role: string;
    result: string;
    text: string;
    seed: string;
}

export interface LandingFAQ {
    question: string;
    answer: string;
}

/**
 * AEO block. Answer engines retrieve passages, not pages: a self-contained
 * lead that defines the entity and answers the head query in the first two or
 * three sentences is what gets lifted into a citation. Rendered directly under
 * the hero so it is the first prose on the page.
 */
export interface LandingQuickAnswer {
    /** Phrase as the question a user would actually type, not as a marketing claim. */
    h2: string;
    /** 2-3 sentences. Must stand alone without the surrounding page. */
    answer: string;
    /** Short label/value pairs. Verifiable facts only - no unsourced numbers. */
    facts?: { label: string; value: string }[];
}

/**
 * AEO block. A 200-400 word self-contained explainer, which is the passage size
 * retrieval systems chunk to. The feature cards are ~30 words each and are too
 * thin to survive chunking on their own.
 */
export interface LandingExplainer {
    h2: string;
    paragraphs: string[];
}

export interface TemplateTranslations {
    // Nav
    navFeatures: string;
    navPricing: string;
    navFaq: string;
    navLogin: string;
    // Hero
    heroBadge: string;
    heroCtaPrimary: string;
    heroCtaSecondary: string;
    heroTrust: string;
    heroMicrocopy: string;
    // Hero visual floating cards
    heroLiveLabel: string;
    heroTrafficIncrease: string;
    heroActiveLabel: string;
    heroVisitorsServed: string;
    // Social proof
    socialProofLabel: string;
    // Problem section
    problemWithout: string;
    problemWith: string;
    problemBullets: string[];
    // Stats card
    statsCampaigns: string;
    statsCountries: string;
    statsUptime: string;
    statsSupport: string;
    // Features
    featuresSubtitle: string;
    // How it works
    howItWorksH2: string;
    howItWorksSteps: { title: string; desc: string }[];
    // Pricing
    pricingH2: string;
    pricingSubtitle: string;
    pricingMostPopular: string;
    pricingVisitors: string;
    pricingGetStarted: string;
    pricingGuarantee: string;
    pricingDelivery: string;
    pricingCountries: string;
    // FAQ
    faqH2: string;
    // CTA
    ctaSubtitle: string;
    ctaButtonPrimary: string;
    ctaButtonSecondary: string;
    ctaMicrocopy: string;
    // Footer
    footerDescription: string;
    footerRelatedServices: string;
    footerCompany: string;
    footerAbout: string;
    footerBlog: string;
    footerHelpCenter: string;
    footerPrivacy: string;
    footerImpressum: string;
    footerStatus: string;
    footerBuyTraffic: string;
}

const DEFAULT_TRANSLATIONS: TemplateTranslations = {
    navFeatures: 'Features',
    navPricing: 'Pricing',
    navFaq: 'FAQ',
    navLogin: 'Dashboard Login',
    heroBadge: 'Premium Quality Traffic',
    heroCtaPrimary: 'Get Your Free Trial',
    heroCtaSecondary: 'View Pricing',
    heroTrust: 'Used by campaign teams',
    heroMicrocopy: 'No credit card required. GA4-visible delivery with quality controls.',
    heroLiveLabel: 'Live',
    heroTrafficIncrease: 'Traffic Increase',
    heroActiveLabel: 'Active',
    heroVisitorsServed: 'Visitors Served',
    socialProofLabel: 'Trusted by:',
    problemWithout: 'Without',
    problemWith: 'With',
    problemBullets: [
        'Low organic traffic limits your growth',
        'Poor search rankings hurt conversions',
        'Competitors compete for your target keywords',
        'High bounce rates weaken engagement analysis',
    ],
    statsCampaigns: 'Daily Active Campaigns',
    statsCountries: 'Countries',
    statsUptime: 'Uptime',
    statsSupport: 'Support',
    featuresSubtitle: 'Everything you need to support website performance analysis and search visibility planning.',
    howItWorksH2: '3 Simple Steps',
    howItWorksSteps: [
        { title: 'Configure Your Campaign', desc: 'Choose country, device, and behavior settings in minutes.' },
        { title: 'Launch the Network', desc: 'Sit back as our platform routes residential visitors to your URL with configurable browsing behavior.' },
        { title: 'Analyze the Results', desc: 'Monitor GA4-visible sessions, engagement time, and campaign quality as traffic flows.' },
    ],
    pricingH2: 'Transparent',
    pricingSubtitle: 'Credits never expire.',
    pricingMostPopular: 'Most Popular',
    pricingVisitors: 'visitors',
    pricingGetStarted: 'Get Started',
    pricingGuarantee: '30-Day Money-Back Guarantee',
    pricingDelivery: 'Instant Delivery (~5 min)',
    pricingCountries: '190+ Countries',
    faqH2: 'Frequently Asked Questions',
    ctaSubtitle: 'Launch a campaign and track delivery quality in your analytics today.',
    ctaButtonPrimary: 'Get Started Free',
    ctaButtonSecondary: 'View Pricing',
    ctaMicrocopy: 'No credit card required. GA4-visible delivery with quality controls.',
    footerDescription: 'solutions for SEO professionals and digital agencies worldwide.',
    footerRelatedServices: 'Related Services',
    footerCompany: 'Company',
    footerAbout: 'About Us',
    footerBlog: 'Blog',
    footerHelpCenter: 'Help Center',
    footerPrivacy: 'Privacy Policy',
    footerImpressum: 'Impressum',
    footerStatus: 'All Systems Operational',
    footerBuyTraffic: 'Buy Website Traffic',
};

export interface LandingPageConfig {
    // SEO
    slug: string;
    titleTag: string;
    metaDescription: string;
    keywords: string;
    primaryKeyword: string;
    secondaryKeyword: string;
    reviewCount: string;
    ratingValue?: string;
    // Hero
    h1: string;
    subheadline: string;
    // Section H2s
    problemH2: string;
    problemText: string;
    solutionPoints: string[];
    featuresH2: string;
    features: LandingFeature[];
    testimonialsH2: string;
    testimonials: LandingTestimonial[];
    ctaH2: string;
    // FAQs
    faqs: LandingFAQ[];
    // Internal cross-links shown in footer
    relatedLinks: { label: string; to: string }[];
    // Schema
    productName: string;
    productDescription: string;
    // SEO/Credit-based pricing — replaces standard visitor pricing when set
    seoPricing?: SeoPricingConfig;
    // Reseller tier pricing — replaces standard pricing when set
    resellerPricing?: ResellerPricingConfig;
    // Override hero CTA links (use full URL for external, or path for internal)
    heroPrimaryHref?: string;
    heroSecondaryHref?: string;
    // Override bottom (final section) CTA links
    ctaBottomPrimaryHref?: string;
    ctaBottomSecondaryHref?: string;
    // Localization — optional, English defaults used if not provided
    translations?: Partial<TemplateTranslations>;
    // AEO — answer-first block under the hero
    quickAnswer?: LandingQuickAnswer;
    // AEO — self-contained explainer passage
    explainer?: LandingExplainer;
    // AEO — ISO date (YYYY-MM-DD). Feeds schema dateModified and a visible line.
    lastUpdated?: string;
}

/**
 * Absolutaussagen zur Sicherheit, je Sprache.
 *
 * Warum das noetig ist: die Regelliste in `softenSeoClaimText` ist englisch.
 * Auf Prod gemessen am 2026-08-22 standen die gleichen Versprechen weiter in
 * jeder anderen Sprache — „absolut sicher" (de, 8 Seiten), „100%安全" (ja, 45),
 * „hoàn toàn an toàn" (vi, 35), „volledig veilig" (nl, 24), „100% seguro"
 * (es/pt, 40), „绝对安全" (zh, 8) und weitere, zusammen rund 200 Seiten.
 *
 * Ersetzt wird jeweils gegen dieselbe Kalibrierung wie im Englischen
 * (`entirely safe` -> `risk-controlled`): kein Versprechen, sondern die
 * Aussage, dass Risiken kontrolliert werden. Endungen sind absichtlich
 * grosszuegig gefasst, weil die Copy die Adjektive flektiert.
 */
const LOCALIZED_CLAIM_RULES: Record<string, [RegExp, string][]> = {
    de: [
        [/Rankings?\s+(?:verbessern|steigern)/gi, 'Sichtbarkeit auswerten'],
        [/Platzierungen\s+verbessern/gi, 'Sichtbarkeit auswerten'],
        [/nat[üu]rliches?\s+Nutzerverhalten/g, 'konfigurierbares Nutzerverhalten'],
        [/nat[üu]rliche\s+Besuchermuster/g, 'konfigurierbare Besuchermuster'],
        [/ununterscheidbar/gi, 'normalem Surfverhalten nachempfunden'],
        [/(?:absolut|voellig|völlig|vollkommen|hundertprozentig)\s+sicher/gi, 'risikokontrolliert'],
        [/100\s*%\s*sicher/gi, 'risikokontrolliert'],
        [/kein(?:erlei)?\s+Risiko/gi, 'mit Risikokontrollen'],
    ],
    es: [
        [/mejorar\s+(?:tu|su|tus|sus)\s+(?:posicionamiento|rankings?|posiciones)/gi, 'analizar tu visibilidad'],
        [/indistinguibles?/gi, 'similar a la navegación normal'],
        [/comportamiento humano natural/gi, 'comportamiento de navegación configurable'],
        [/(?:totalmente|completamente|absolutamente)\s+segur[oa]s?/gi, 'con controles de riesgo'],
        [/100\s*%\s*segur[oa]s?/gi, 'con controles de riesgo'],
        [/sin\s+ning[uú]n\s+riesgo/gi, 'con controles de riesgo'],
    ],
    pt: [
        [/melhorar\s+(?:o\s+)?(?:seu|teu)\s+rankings?/gi, 'analisar a sua visibilidade'],
        [/indistinguíve(?:l|is)/g, 'semelhante à navegação normal'],
        [/comportamento humano natural/gi, 'comportamento de navegação configurável'],
        [/(?:totalmente|completamente|absolutamente)\s+segur[oa]s?/gi, 'com controlos de risco'],
        [/100\s*%\s*segur[oa]s?/gi, 'com controlos de risco'],
        [/sem\s+qualquer\s+risco/gi, 'com controlos de risco'],
    ],
    fr: [
        [/améliorer\s+votre\s+classement/g, 'analyser votre visibilité'],
        [/indiscernables?/gi, 'semblable à une navigation normale'],
        [/comportement humain naturel/gi, 'comportement de navigation configurable'],
        [/num[ée]ro\s*1\s+/g, ''],
        [/(?:le\s+)?Rang\s*#1/gi, 'la visibilité'],
        [/(?:totalement|entièrement|complètement|absolument)\s+s[ûu]re?s?/gi, 'encadré par des contrôles de risque'],
        [/100\s*%\s*s[ûu]re?s?/gi, 'encadré par des contrôles de risque'],
        [/aucun\s+risque/gi, 'des contrôles de risque'],
    ],
    it: [
        [/migliorare\s+il\s+posizionamento/gi, 'analizzare la visibilità'],
        [/comportamento umano naturale/gi, 'comportamento di navigazione configurabile'],
        [/(?:totalmente|completamente|assolutamente)\s+sicur[oa]i?/gi, 'con controlli di rischio'],
        [/100\s*%\s*sicur[oa]i?/gi, 'con controlli di rischio'],
        [/nessun\s+rischio/gi, 'controlli di rischio'],
    ],
    nl: [
        [/hogere\s+ranking/gi, 'duidelijkere engagementdata'],
        [/natuurlijk menselijk gedrag/gi, 'instelbaar surfgedrag'],
        [/(?:volledig|volkomen|helemaal|absoluut)\s+veilig/gi, 'met risicobeheersing'],
        [/100\s*%\s*veilig/gi, 'met risicobeheersing'],
        [/geen\s+enkel\s+risico/gi, 'met risicobeheersing'],
    ],
    sv: [
        [/(?:helt|fullstaendigt|fullständigt|absolut)\s+s[aä]kert/gi, 'riskkontrollerat'],
        [/100\s*%\s*s[aä]kert/gi, 'riskkontrollerat'],
        [/ingen\s+risk/gi, 'riskkontroller'],
    ],
    pl: [
        [/naturalne\s+zachowanie(\s+\w+)?/gi, 'konfigurowalne zachowanie w przeglądarce'],
        [/poprawić\s+pozycj\w*/g, 'analizować widoczność'],
        [/(?:całkowicie|calkowicie|zupełnie|absolutnie)\s+bezpieczn\w*/gi, 'z kontrolą ryzyka'],
        [/100\s*%\s*bezpieczn\w*/gi, 'z kontrolą ryzyka'],
        [/bez\s+żadnego\s+ryzyka/gi, 'z kontrolą ryzyka'],
    ],
    cs: [
        [/zlepšit\s+pozice/g, 'analyzovat viditelnost'],
        [/přirozené\s+chování/g, 'nastavitelné chování v prohlížeči'],
        [/(?:zcela|naprosto|úplně|uplne)\s+bezpe\w*/gi, 's kontrolou rizik'],
        [/100\s*%\s*bezpe\w*/gi, 's kontrolou rizik'],
        [/bez\s+jak[ée]hokoli\w*\s+rizika/gi, 's kontrolou rizik'],
    ],
    ro: [
        [/îmbunătăți\s+clasament\w*/g, 'analiza vizibilitatea'],
        [/comportament uman natural/gi, 'comportament de navigare configurabil'],
        [/(?:complet|absolut|total)\s+sigur\w*/gi, 'cu controale de risc'],
        [/100\s*%\s*sigur\w*/gi, 'cu controale de risc'],
        [/f[aă]r[aă]\s+niciun\s+risc/gi, 'cu controale de risc'],
    ],
    ru: [
        [/(?:повысить|улучшить|поднять)\s+позици\w*/g, 'анализировать видимость'],
        [/естественное поведение/g, 'настраиваемое поведение в браузере'],
        [/(?:полностью|абсолютно|совершенно)\s+безопасн\w*/gi, 'с контролем рисков'],
        [/100\s*%\s*безопасн\w*/gi, 'с контролем рисков'],
        [/без\s+(?:какого-либо\s+)?риска/gi, 'с контролем рисков'],
    ],
    tr: [
        [/s[ıi]ralaman[ıi]z[ıi]\s+y[üu]kselt\w*/g, 'görünürlüğünüzü analiz edin'],
        [/doğal kullanıcı davranış\w*/g, 'yapılandırılabilir tarama davranışı'],
        [/(?:tamamen|tamamıyla|kesinlikle)\s+güvenli\w*/gi, 'risk kontrollü'],
        [/100\s*%\s*güvenli\w*/gi, 'risk kontrollü'],
        [/hiçbir\s+risk\w*\s+yok\w*/gi, 'risk kontrolleri var'],
    ],
    vi: [
        [/(?:cải thiện|tăng)\s+thứ hạng/g, 'phân tích khả năng hiển thị'],
        [/hành vi người dùng tự nhiên/g, 'hành vi duyệt web có thể cấu hình'],
        [/(?:hoàn toàn|tuyệt đối)\s+an toàn/gi, 'được kiểm soát rủi ro'],
        [/an toàn\s+100\s*%/gi, 'được kiểm soát rủi ro'],
        [/100\s*%\s*an toàn/gi, 'được kiểm soát rủi ro'],
        [/không\s+có\s+rủi ro/gi, 'có kiểm soát rủi ro'],
    ],
    id: [
        [/meningkatkan\s+peringkat/gi, 'menganalisis visibilitas'],
        [/perilaku pengguna alami/gi, 'perilaku penjelajahan yang dapat dikonfigurasi'],
        [/(?:sepenuhnya|sangat|benar-benar)\s+aman/gi, 'dengan kontrol risiko'],
        [/100\s*%\s*aman/gi, 'dengan kontrol risiko'],
        [/tanpa\s+risiko/gi, 'dengan kontrol risiko'],
    ],
    tl: [
        [/(?:ganap|lubos)(?:an)?g?\s+ligtas/gi, 'may kontrol sa panganib'],
        [/100\s*%\s*ligtas/gi, 'may kontrol sa panganib'],
    ],
    th: [
        [/ปลอดภัย\s*100\s*%/g, 'มีการควบคุมความเสี่ยง'],
        [/ปลอดภัยอย่างสมบูรณ์/g, 'มีการควบคุมความเสี่ยง'],
    ],
    ja: [
        [/(?:ランキング|順位)を上げ/g, '可視性を分析し'],
        [/検索順位の第一位を奪取/g, '検索での可視性を測定'],
        [/自然なユーザー行動/g, '設定可能なブラウジング動作'],
        [/見分けがつかない/g, '通常の閲覧に近い'],
        [/(?:検索エンジンの?アルゴリズム(?:も|を))欺く/g, '通常の閲覧に近づける'],
        [/100\s*%\s*安全/g, 'リスク管理済み'],
        [/完全に安全/g, 'リスク管理済み'],
        [/絶対に安全/g, 'リスク管理済み'],
    ],
    ko: [
        [/순위를\s*올리\w*/g, '가시성을 분석'],
        [/자연스러운 사용자 행동/g, '구성 가능한 탐색 동작'],
        [/압도적\s*1위/g, '측정 가능한 지표'],
        [/100\s*%\s*안전/g, '위험 관리 적용'],
        [/완전히\s*안전/g, '위험 관리 적용'],
    ],
    zh: [
        [/(?:提升|提高)排名/g, '分析可见度'],
        [/提升至第一名/g, '衡量搜索可见度'],
        [/自然的用户行为/g, '可配置的浏览行为'],
        [/无法区分/g, '接近正常浏览'],
        [/绝对安全/g, '经风险控制'],
        [/100\s*%\s*安全/g, '经风险控制'],
        [/完全安全/g, '经风险控制'],
    ],
    he: [
        [/בטוח\s+לחלוטין/g, 'מבוקר סיכון'],
    ],
    ar: [
        [/آمن\s+(?:تمامًا|تماما|بنسبة\s*100\s*%)/g, 'مع ضوابط للمخاطر'],
    ],
    hi: [
        [/(?:पूरी तरह|पूर्णतः)\s*सुरक्षित/g, 'जोखिम-नियंत्रित'],
        [/100\s*%\s*सुरक्षित/g, 'जोखिम-नियंत्रित'],
    ],
    bn: [
        [/(?:সম্পূর্ণ|পুরোপুরি)\s*নিরাপদ/g, 'ঝুঁকি-নিয়ন্ত্রিত'],
        [/100\s*%\s*নিরাপদ/g, 'ঝুঁকি-নিয়ন্ত্রিত'],
    ],
};

const applyLocalizedClaimRules = (value: string, lang: string): string => {
    const rules = LOCALIZED_CLAIM_RULES[lang];
    if (!rules) return value;
    return rules.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), value);
};

const softenEnglishClaimText = (value: string, lang: string = 'en'): string => normalizePublicPricingCopy(value, lang)
    .replace(/#1\s+/gi, '')
    .replace(/ranked\s+#1/gi, 'tracked stronger local visibility')
    .replace(/Ranked Local Sites Faster/gi, 'Improved local reporting faster')
    .replace(/Zero Penalties/gi, 'Cleaner risk controls')
    .replace(/Dominate\s+/gi, 'Compete in ')
    .replace(/dominate\s+/gi, 'compete in ')
    .replace(/100%\s+Safe\s*&\s*Analytics-Verified/gi, 'Analytics-visible delivery.')
    .replace(/100%\s+Analytics\s+Safe/gi, 'GA4-visible delivery')
    .replace(/100%\s+safe/gi, 'risk-controlled')
    .replace(/100%\s+safety/gi, 'risk controls')
    .replace(/100%\s+בטוח/gi, 'מבוקר סיכון')
    .replace(/100%\s+natural/gi, 'policy-aware')
    .replace(/completely safe/gi, 'risk-controlled')
    .replace(/entirely safe/gi, 'risk-controlled')
    .replace(/safe for SEO/gi, 'built for analytics testing')
    .replace(/zero risk of Google penalties/gi, 'built with controls for obvious bot-pattern risk')
    .replace(/no risk to your website or ad accounts/gi, 'with safeguards for analytics quality and account policy review')
    .replace(/no risk of bans, shadow-banning, or policy violations/gi, 'with safeguards for account-policy review')
    // Am 2026-08-22 auf Prod nachgemessen: diese vier Formulierungen liefen an
    // allen bestehenden Regeln vorbei und standen live als absolutes Versprechen.
    .replace(/zero risk to my account/gi, 'no account issues so far')
    .replace(/zero risk to your project(?:'|\u2019)s reputation/gi, 'controls for reputation risk')
    .replace(/Zero risk of penalties\.?/gi, 'Controls for obvious penalty risk.')
    .replace(/There(?:'|\u2019)s no risk to your Google Analytics, AdSense, or search rankings/gi, 'It is built with safeguards for analytics quality and for AdSense and search policy review')
    .replace(/indistinguishable from (?:organic|genuine search|real human|real) (?:visitors|traffic|users|user behavior)/gi, 'designed to resemble normal browsing patterns')
    .replace(/indistinguishable from real citizens browsing/gi, 'designed to resemble regional browsing patterns')
    .replace(/looks?\s+100%\s+natural/gi, 'uses policy-aware browsing patterns')
    .replace(/appears?\s+100%\s+natural/gi, 'appears as configurable browsing activity')
    .replace(/appear(?:s)?\s+(?:completely\s+)?natural(?:ly)?/gi, 'appears as configurable browsing activity')
    .replace(/exactly like organic traffic/gi, 'with comparable GA4 session fields')
    .replace(/will never be penalized/gi, 'uses risk controls to avoid obvious bot patterns')
    .replace(/cannot trigger any algorithmic penalties/gi, 'is designed to reduce obvious bot-pattern risk')
    .replace(/cannot penalize/gi, 'is designed to reduce risk for')
    .replace(/completely undetectable/gi, 'designed to avoid obvious bot patterns')
    .replace(/undetectable/gi, 'designed to avoid obvious bot patterns')
    .replace(/bypass(?:es|ing)?\s+Google'?s\s+click fraud filters/gi, 'reduce obvious click-pattern issues')
    .replace(/(?:entirely|fully|completely)?\s*bypasses detection mechanisms/gi, 'uses controls for obvious detection patterns')
    .replace(/traffic entirely bypasses detection mechanisms/gi, 'traffic uses controls for obvious detection patterns')
    .replace(/Google detecting unnatural CTR behavior/gi, 'quality systems flagging unnatural click patterns')
    .replace(/CTR manipulation/gi, 'CTR analysis')
    .replace(/manipulate CTR/gi, 'analyze CTR')
    .replace(/organic search signals/gi, 'organic-search analytics')
    .replace(/GMB ranking booster/gi, 'Google Business Profile analytics booster')
    .replace(/domain authority/gi, 'domain analytics profile')
    .replace(/boost your SEO rankings/gi, 'support SEO research and analytics validation')
    .replace(/boosts your SEO rankings/gi, 'supports SEO research and analytics validation')
    .replace(/boost your rankings/gi, 'support visibility analysis')
    .replace(/boost your ranking/gi, 'support visibility analysis')
    .replace(/boost your search engine rankings/gi, 'support search visibility analysis')
    .replace(/boost your online visibility instantly/gi, 'support search visibility analysis')
    .replace(/boost (?:your )?(?:local and regional )?search rankings/gi, 'support search visibility analysis')
    .replace(/boost(?:ed|ing)? my rankings/gi, 'improved my analytics readout')
    .replace(/boost(?:ed|ing)? (?:both )?(?:your|our|my)?\s*(?:social presence and )?SEO rankings/gi, 'supports SEO and referral analytics')
    .replace(/improved our rankings/gi, 'improved our campaign analysis')
    .replace(/tripled because the engagement signals boosted my rankings/gi, 'became easier to analyze after engagement data improved')
    .replace(/jumped from page 3 to page 1/gi, 'showed clearer engagement trends over time')
    .replace(/took us from page 3 to the top 3 spots/gi, 'helped us evaluate local engagement trends')
    .replace(/boosting your rankings/gi, 'supporting visibility analysis')
    .replace(/boost Naver rankings/gi, 'support Naver visibility analysis')
    .replace(/boost your CTR and Blog rankings/gi, 'support CTR and blog visibility analysis')
    .replace(/boost your website's performance and search engine rankings/gi, 'support website performance analysis and search visibility planning')
    .replace(/ranking signals/gi, 'engagement metrics')
    .replace(/ranking signal/gi, 'engagement metric')
    .replace(/stronger ranking signals/gi, 'clearer engagement metrics')
    .replace(/regional ranking signals/gi, 'regional engagement metrics')
    .replace(/positive ranking signal/gi, 'positive engagement metric')
    .replace(/signals popularity, boosting your rankings/gi, 'helps analyze regional interest patterns')
    .replace(/authority signals/gi, 'authority-adjacent analytics')
    .replace(/social signals/gi, 'social referral analytics')
    .replace(/higher organic rankings/gi, 'clearer engagement data for SEO analysis')
    .replace(/climb in search results/gi, 'measure engagement quality over time')
    .replace(/improve your Google Analytics metrics, dwell time, and organic rankings/gi, 'support GA4-visible analytics validation and engagement analysis')
    .replace(/improve dwell time and CTR/gi, 'support dwell-time and CTR analysis')
    .replace(/Google rewards/gi, 'you can measure')
    .replace(/what Google looks for when deciding rankings/gi, 'useful for engagement analysis')
    .replace(/maximum SEO impact/gi, 'more relevant campaign testing')
    .replace(/powerful SEO signals/gi, 'measurable engagement signals')
    .replace(/send powerful SEO signals/gi, 'create measurable engagement data')
    .replace(/moved rankings/gi, 'improved campaign analysis')
    .replace(/rankings followed naturally/gi, 'engagement trends became easier to evaluate')
    .replace(/Google has no reason to rank you higher/gi, 'it is harder to evaluate engagement opportunities')
    .replace(/Poor search rankings hurt conversions/gi, 'Weak search visibility hurts conversions')
    .replace(/High bounce rates damage SEO/gi, 'High bounce rates weaken engagement analysis')
    .replace(/lower bounce rates for stronger engagement metrics/gi, 'analyze bounce-rate trends and engagement metrics')
    .replace(/to look entirely natural to search engines/gi, 'for controlled analytics testing')
    .replace(/simulating natural human behavior/gi, 'using configurable browsing behavior')
    .replace(/mimicking natural user behavior/gi, 'using configurable browsing behavior')
    .replace(/natural browser behavior/gi, 'configurable browser behavior')
    .replace(/natural browsing behavior/gi, 'configurable browsing behavior')
    .replace(/natural session behavior/gi, 'configurable session behavior')
    .replace(/natural dwell time/gi, 'configurable dwell time')
    .replace(/natural dwell times/gi, 'configurable dwell times')
    .replace(/natural growth curve/gi, 'steady delivery curve')
    .replace(/natural engagement signals/gi, 'configurable engagement metrics')
    .replace(/genuine browser fingerprints/gi, 'standard browser fingerprints')
    .replace(/genuine organic search visit/gi, 'measurable organic-search session')
    .replace(/real behavioral signals/gi, 'measurable behavioral data')
    .replace(/real social referral traffic that improved our rankings/gi, 'social referral traffic that improved our reporting')
    .replace(/authentic-looking social signals/gi, 'measurable social referral analytics')
    .replace(/exact CTR manipulation features required/gi, 'CTR analysis controls used')
    .replace(/Watch your metrics improve in Google Analytics 4 as traffic flows and engagement signals strengthen/gi, 'Monitor GA4-visible sessions, engagement time, and campaign quality as traffic flows');

const softenSeoClaimText = (value: string, lang: string = 'en'): string =>
    applyLocalizedClaimRules(softenEnglishClaimText(value, lang), lang);

/**
 * Sprache aus dem Routenpraefix. `/de/website-traffic-kaufen` -> `de`,
 * `/alternatives/serpclix` -> `en`. Die Preis-Ersetzung braucht sie, weil sie
 * sonst einen englischen Halbsatz in eine fremdsprachige Meta-Description
 * schreibt (am 2026-08-22 auf 42 Seiten nachgewiesen).
 */
export const landingLanguageFromPath = (pathname: string): string => {
    const match = /^\/([a-z]{2})(?:\/|$)/.exec(pathname);
    return match ? match[1] : 'en';
};

const softenTranslations = (translations: TemplateTranslations, lang: string = 'en'): TemplateTranslations => {
    const soften = (value: string): string => softenSeoClaimText(value, lang);
    /*
     * Frueher liefen hier nur sechs der 47 Felder durch den Filter. Aufgefallen
     * ist es am 2026-08-22 an `heroBadge`: dort stand auf 18 Seiten „#1 Traffic
     * Bots" bzw. „#1 Bots de Trafic" — die Regel `#1\s+` haette das entfernt,
     * sie wurde nur nie auf das Feld angewandt. Jetzt geht jeder String durch,
     * Arrays und die Schritt-Objekte eingeschlossen. Labels wie „Pricing" oder
     * „FAQ" passen auf keine Regel und bleiben unveraendert.
     */
    const result: Record<string, unknown> = { ...translations };
    for (const [key, value] of Object.entries(translations)) {
        if (typeof value === 'string') {
            result[key] = soften(value);
        } else if (Array.isArray(value)) {
            result[key] = value.map((entry) => (
                typeof entry === 'string'
                    ? soften(entry)
                    : entry && typeof entry === 'object'
                        ? Object.fromEntries(Object.entries(entry).map(([k, v]) => [k, typeof v === 'string' ? soften(v) : v]))
                        : entry
            ));
        }
    }
    return result as unknown as TemplateTranslations;
};

const softenLandingConfig = (config: LandingPageConfig, lang: string = 'en'): LandingPageConfig => {
    const soften = (value: string): string => softenSeoClaimText(value, lang);
    return ({
    ...config,
    titleTag: soften(config.titleTag),
    metaDescription: soften(config.metaDescription),
    h1: soften(config.h1),
    subheadline: soften(config.subheadline),
    problemH2: soften(config.problemH2),
    problemText: soften(config.problemText),
    solutionPoints: config.solutionPoints.map((value) => soften(value)),
    featuresH2: soften(config.featuresH2),
    features: config.features.map(feature => ({
        ...feature,
        title: soften(feature.title),
        description: soften(feature.description),
    })),
    testimonialsH2: soften(config.testimonialsH2),
    testimonials: config.testimonials.map(testimonial => ({
        ...testimonial,
        result: soften(testimonial.result),
        text: soften(testimonial.text),
    })),
    ctaH2: soften(config.ctaH2),
    faqs: config.faqs.map(faq => ({
        question: soften(faq.question),
        answer: soften(faq.answer),
    })),
    relatedLinks: config.relatedLinks.filter(link => PUBLIC_LANDING_PATHS.has(link.to)).map(link => ({
        ...link,
        label: soften(link.label),
    })),
    quickAnswer: config.quickAnswer && {
        ...config.quickAnswer,
        h2: soften(config.quickAnswer.h2),
        answer: soften(config.quickAnswer.answer),
        facts: config.quickAnswer.facts?.map(f => ({ ...f, value: soften(f.value) })),
    },
    explainer: config.explainer && {
        h2: soften(config.explainer.h2),
        paragraphs: config.explainer.paragraphs.map((value) => soften(value)),
    },
    productName: soften(config.productName),
    productDescription: soften(config.productDescription),
    });
};

const TrafficLandingLegacy: React.FC<{ config: LandingPageConfig }> = ({ config }) => {
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [selectedTier, setSelectedTier] = useState<TierId>('professional');
    const location = useLocation();
    const brand = getBrand();
    const landingLang = landingLanguageFromPath(location.pathname);
    const safeConfig = softenLandingConfig(config, landingLang);
    const t = softenTranslations({ ...DEFAULT_TRANSLATIONS, ...config.translations }, landingLang);

    const canonicalPath = location.pathname === '/'
        ? '/'
        : location.pathname.replace(/\/+$/, '');
    const canonicalUrl = `${brand.website}${canonicalPath}`;
    const defaultOfferVolumes = [60000, 300000, 600000, 1000000];
    const schemaOffers = safeConfig.seoPricing
        ? safeConfig.seoPricing.packages.map((pkg) => ({
            '@type': 'Offer',
            name: `${pkg.credits.toLocaleString('en-US')} ${safeConfig.seoPricing?.unitLabel ?? 'credits'}`,
            price: (pkg.credits * pkg.price).toFixed(2),
            priceCurrency: 'USD',
            url: canonicalUrl,
            eligibleQuantity: {
                '@type': 'QuantitativeValue',
                value: pkg.credits + pkg.free,
                unitText: safeConfig.seoPricing?.unitLabel ?? 'credits',
            },
        }))
        : safeConfig.resellerPricing
            ? safeConfig.resellerPricing.tiers.flatMap((tier) => {
                const numericPrice = Number(tier.monthlyPrice.replace(/[^0-9.]/g, ''));
                if (!Number.isFinite(numericPrice) || numericPrice <= 0) return [];
                return [{
                    '@type': 'Offer',
                    name: `${tier.name} monthly plan`,
                    price: numericPrice.toFixed(2),
                    priceCurrency: tier.monthlyPrice.includes('€') ? 'EUR' : 'USD',
                    url: tier.ctaHref?.startsWith('http') ? tier.ctaHref : canonicalUrl,
                }];
            })
            : defaultOfferVolumes.map((volume) => ({
                '@type': 'Offer',
                name: `Professional ${formatVolume(volume)} Hit Pack`,
                price: PRICING_MATRIX.professional[volume][1].toFixed(2),
                priceCurrency: 'USD',
                url: canonicalUrl,
                eligibleQuantity: {
                    '@type': 'QuantitativeValue',
                    value: volume,
                    unitText: 'hits',
                },
            }));

    const schema = [
        {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            name: safeConfig.productName,
            description: safeConfig.productDescription,
            url: canonicalUrl,
            isPartOf: { '@id': `${brand.website}/#website` },
            mainEntity: { '@id': `${canonicalUrl}#service` },
            // Freshness signal. Answer engines weight recency: leaving it out
            // means the page dates itself by whatever the crawler last saw.
            ...(safeConfig.lastUpdated ? { dateModified: safeConfig.lastUpdated } : {}),
            publisher: { '@id': `${brand.website}/#organization` },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'Service',
            '@id': `${canonicalUrl}#service`,
            name: safeConfig.productName,
            description: safeConfig.productDescription,
            serviceType: safeConfig.primaryKeyword,
            url: canonicalUrl,
            provider: {
                '@type': 'Organization',
                name: brand.name,
                url: brand.website,
            },
            hasOfferCatalog: {
                '@type': 'OfferCatalog',
                name: `${safeConfig.productName} offers`,
                itemListElement: schemaOffers,
            },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: safeConfig.faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
        },
        {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${brand.website}` },
                { '@type': 'ListItem', position: 2, name: safeConfig.primaryKeyword, item: canonicalUrl },
            ],
        },
    ];

    const isRTL = canonicalPath.startsWith('/he/') || canonicalPath.startsWith('/ar/');

    return (
        <div className="min-h-screen bg-white text-gray-900 font-sans" dir={isRTL ? 'rtl' : undefined}>
            <SEO
                title={safeConfig.titleTag}
                description={safeConfig.metaDescription}
                keywords={safeConfig.keywords}
                schema={schema}
            />

            {/* Nav */}
            <nav className="border-b border-gray-100 bg-white/95 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 group">
                        <span className="text-2xl font-black tracking-tight group-hover:scale-105 transition-transform" style={{ color: brand.primaryColor }}>{brand.logoText}</span>
                        <span className="hidden sm:inline-block text-xs font-bold text-white px-2 py-1 rounded-sm uppercase tracking-wide" style={{ backgroundColor: brand.primaryColor }}>{brand.logoAccent}</span>
                    </Link>
                    <div className="flex items-center gap-6">
                        <div className="hidden md:flex gap-6 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#features" className="hover:text-[#ff5a0a] transition-colors">{t.navFeatures}</a>
                            <a href="#pricing" className="hover:text-[#ff5a0a] transition-colors">{t.navPricing}</a>
                            <a href="#faq" className="hover:text-[#ff5a0a] transition-colors">{t.navFaq}</a>
                        </div>
                        <Link to="/login" className="text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-black transition-colors shadow-md rounded-sm" style={{ backgroundColor: brand.primaryColor }}>
                            {t.navLogin}
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative pt-24 pb-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 70%, #0a0a0a 100%)' }}>
                {/* Animated gradient mesh */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]" style={{ background: brand.primaryColor }} />
                    <div className="absolute bottom-[-30%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-10 blur-[100px] bg-blue-500" />
                    <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] rounded-full opacity-10 blur-[80px] bg-purple-500" />
                    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
                </div>

                <div className="max-w-7xl mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2.5 bg-white/[0.08] border border-white/[0.12] backdrop-blur-sm px-5 py-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8 rounded-full text-white/80">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#22c55e' }} />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                            </span>
                            {t.heroBadge}
                        </div>

                        <h1 className="text-5xl md:text-[4.5rem] font-black tracking-tight leading-[1.0] mb-6 text-white" elementtiming="lcp">
                            {safeConfig.h1.split(safeConfig.primaryKeyword).map((part, i, arr) => (
                                <span key={i}>{part}{i < arr.length - 1 && <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] via-orange-400 to-amber-400">{safeConfig.primaryKeyword}</span>}</span>
                            ))}
                        </h1>

                        <p className="text-lg text-white/50 mb-10 max-w-lg leading-relaxed">{safeConfig.subheadline}</p>

                        <div className="flex flex-col sm:flex-row gap-4 mb-8">
                            {safeConfig.heroPrimaryHref ? (
                                <a href={safeConfig.heroPrimaryHref} target={safeConfig.heroPrimaryHref.startsWith('http') ? '_blank' : undefined} rel={safeConfig.heroPrimaryHref.startsWith('http') ? 'noreferrer' : undefined} className="group relative text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-3 transform hover:-translate-y-0.5 rounded-lg overflow-hidden" style={{ backgroundColor: brand.primaryColor }}>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                                    <span className="relative z-10 flex items-center gap-3">{t.heroCtaPrimary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                                    <div className="absolute inset-0 rounded-lg shadow-[0_0_30px_rgba(255,90,10,0.4)] group-hover:shadow-[0_0_50px_rgba(255,90,10,0.6)] transition-shadow" />
                                </a>
                            ) : (
                                <Link to="/signup" className="group relative text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-3 transform hover:-translate-y-0.5 rounded-lg overflow-hidden" style={{ backgroundColor: brand.primaryColor }}>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                                    <span className="relative z-10 flex items-center gap-3">{t.heroCtaPrimary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
                                    <div className="absolute inset-0 rounded-lg shadow-[0_0_30px_rgba(255,90,10,0.4)] group-hover:shadow-[0_0_50px_rgba(255,90,10,0.6)] transition-shadow" />
                                </Link>
                            )}
                            {safeConfig.heroSecondaryHref ? (
                                <a href={safeConfig.heroSecondaryHref} className="group bg-white/[0.06] border border-white/[0.12] backdrop-blur-sm text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white/[0.12] hover:border-white/[0.25] transition-all transform hover:-translate-y-0.5 flex items-center justify-center rounded-lg">
                                    {t.heroCtaSecondary}
                                </a>
                            ) : (
                                <Link to="/signup" className="group bg-white/[0.06] border border-white/[0.12] backdrop-blur-sm text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white/[0.12] hover:border-white/[0.25] transition-all transform hover:-translate-y-0.5 flex items-center justify-center rounded-lg">
                                    {t.heroCtaSecondary}
                                </Link>
                            )}
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="flex -space-x-2.5">
                            {/* Inline SVG avatars — eliminates 5 external HTTP requests from critical path */}
                            {[
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Ccircle cx=%2216%22 cy=%2212%22 r=%228%22 fill=%22%23f97316%22/%3E%3Ccircle cx=%2216%22 cy=%2236%22 r=%2214%22 fill=%22%23f97316%22/%3E%3C/svg%3E',
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Ccircle cx=%2216%22 cy=%2212%22 r=%228%22 fill=%22%236366f1%22/%3E%3Ccircle cx=%2216%22 cy=%2236%22 r=%2214%22 fill=%22%236366f1%22/%3E%3C/svg%3E',
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Ccircle cx=%2216%22 cy=%2212%22 r=%228%22 fill=%22%2310b981%22/%3E%3Ccircle cx=%2216%22 cy=%2236%22 r=%2214%22 fill=%22%2310b981%22/%3E%3C/svg%3E',
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Ccircle cx=%2216%22 cy=%2212%22 r=%228%22 fill=%22%23ec4899%22/%3E%3Ccircle cx=%2216%22 cy=%2236%22 r=%2214%22 fill=%22%23ec4899%22/%3E%3C/svg%3E',
                                'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Ccircle cx=%2216%22 cy=%2212%22 r=%228%22 fill=%22%23eab308%22/%3E%3Ccircle cx=%2216%22 cy=%2236%22 r=%2214%22 fill=%22%23eab308%22/%3E%3C/svg%3E',
                            ].map((uri, i) => (
                                <div key={i} className="w-8 h-8 rounded-full border-2 border-gray-900 bg-gray-700 overflow-hidden ring-1 ring-white/10">
                                    <img src={uri} alt="Customer avatar" width="32" height="32" />
                                </div>
                            ))}
                            </div>
                            <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <ShieldCheck size={13} className="text-emerald-400" />
                                    <span className="text-xs text-white/40 font-medium">Quality controls</span>
                                </div>
                                <div className="text-[11px] font-medium text-white/40">{t.heroTrust}</div>
                            </div>
                        </div>
                        <p className="mt-5 text-[11px] text-white/30 font-medium">{t.heroMicrocopy}</p>
                    </div>

                    {/* Hero Visual — Live Dashboard Mockup */}
                    <div className="relative hidden lg:flex items-center justify-center">
                        <div className="relative w-full max-w-md">
                            {/* Main dashboard card */}
                            <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.1] rounded-2xl p-6 shadow-2xl shadow-black/20">
                                {/* Dashboard header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${brand.primaryColor}20` }}>
                                            <Activity size={16} style={{ color: brand.primaryColor }} />
                                        </div>
                                        <div>
                                            <div className="text-[11px] font-bold text-white/90 uppercase tracking-wider">Campaign Dashboard</div>
                                            <div className="text-[10px] text-white/30">Real-time analytics</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                        <span className="text-[10px] font-bold text-green-400/80 uppercase tracking-wider">{t.heroLiveLabel}</span>
                                    </div>
                                </div>

                                {/* Stats row */}
                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <div className="bg-white/[0.04] rounded-xl p-3 border border-white/[0.06]">
                                        <div className="text-[10px] text-white/30 font-medium mb-1">Sessions</div>
                                        <div className="text-xl font-black text-white">24.8K</div>
                                        <div className="text-[10px] font-bold text-green-400 flex items-center gap-0.5"><TrendingUp size={10} /> +12.4%</div>
                                    </div>
                                    <div className="bg-white/[0.04] rounded-xl p-3 border border-white/[0.06]">
                                        <div className="text-[10px] text-white/30 font-medium mb-1">Bounce Rate</div>
                                        <div className="text-xl font-black text-white">28%</div>
                                        <div className="text-[10px] font-bold text-green-400 flex items-center gap-0.5"><TrendingUp size={10} /> -8.2%</div>
                                    </div>
                                    <div className="bg-white/[0.04] rounded-xl p-3 border border-white/[0.06]">
                                        <div className="text-[10px] text-white/30 font-medium mb-1">Avg. Duration</div>
                                        <div className="text-xl font-black text-white">3:42</div>
                                        <div className="text-[10px] font-bold text-green-400 flex items-center gap-0.5"><TrendingUp size={10} /> +22%</div>
                                    </div>
                                </div>

                                {/* Mini traffic chart */}
                                <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.06] mb-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-[10px] font-bold text-white/50 uppercase tracking-wider">{t.heroTrafficIncrease}</div>
                                        <div className="text-[10px] font-bold text-white/30">Last 7 days</div>
                                    </div>
                                    <div className="flex items-end gap-1 h-16">
                                        {[35, 45, 38, 52, 65, 58, 78, 72, 85, 80, 92, 88, 95, 100].map((h, i) => (
                                            <div key={i} className="flex-1 rounded-sm transition-all duration-500" style={{
                                                height: `${h}%`,
                                                backgroundColor: i >= 12 ? brand.primaryColor : `${brand.primaryColor}${i >= 8 ? '80' : i >= 4 ? '50' : '25'}`,
                                            }} />
                                        ))}
                                    </div>
                                </div>

                                {/* Country breakdown */}
                                <div className="space-y-2.5">
                                    {[{ flag: '🇺🇸', name: 'United States', pct: 34 }, { flag: '🇬🇧', name: 'United Kingdom', pct: 22 }, { flag: '🇩🇪', name: 'Germany', pct: 18 }].map((c, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-sm">{c.flag}</span>
                                            <span className="text-[11px] font-medium text-white/50 w-28 truncate">{c.name}</span>
                                            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                                                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${c.pct}%`, background: `linear-gradient(90deg, ${brand.primaryColor}, ${brand.primaryColor}80)` }} />
                                            </div>
                                            <span className="text-[10px] font-bold text-white/40 w-8 text-right">{c.pct}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Floating notification card */}
                            <div className="absolute -top-4 -right-6 bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] rounded-xl p-3 shadow-2xl shadow-black/30 animate-bounce" style={{ animationDuration: '5s' }}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-500/20"><TrendingUp size={14} className="text-green-400" /></div>
                                    <div>
                                        <div className="text-[10px] font-bold text-white/80">+458% {t.heroTrafficIncrease}</div>
                                        <div className="text-[9px] text-white/30">Last 30 days</div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating GA4 verified badge */}
                            <div className="absolute -bottom-3 -left-6 bg-white/[0.08] backdrop-blur-xl border border-white/[0.12] rounded-xl p-3 shadow-2xl shadow-black/30 animate-bounce" style={{ animationDuration: '6s', animationDelay: '2s' }}>
                                <div className="flex items-center gap-2.5">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-500/20"><ShieldCheck size={14} className="text-blue-400" /></div>
                                    <div>
                                        <div className="text-[10px] font-bold text-white/80">GA4 Verified</div>
                                        <div className="text-[9px] text-white/30">GA4-visible delivery</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="max-w-7xl mx-auto px-6 relative z-10 mt-16">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { value: '50M+', label: t.heroVisitorsServed },
                            { value: '190+', label: t.statsCountries },
                            { value: '99.9%', label: t.statsUptime },
                            { value: '24/7', label: t.statsSupport },
                        ].map((stat, i) => (
                            <div key={i} className="text-center py-4 px-3 bg-white/[0.04] border border-white/[0.06] rounded-xl backdrop-blur-sm">
                                <div className="text-2xl font-black text-white mb-0.5">{stat.value}</div>
                                <div className="text-[10px] font-bold text-white/30 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === AEO: answer-first block ===
                Retrieval systems cite passages, not pages. This is the first
                prose after the hero so that the definition and the direct answer
                are what gets chunked, instead of the marketing headline. */}
            {(safeConfig.quickAnswer || safeConfig.explainer) && (
                <section id="quick-answer" className="bg-white py-16 px-6 border-b border-gray-100">
                    <div className="max-w-3xl mx-auto">
                        {safeConfig.quickAnswer && (
                            <>
                                <h2 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 mb-4">
                                    {safeConfig.quickAnswer.h2}
                                </h2>
                                <p className="text-lg leading-relaxed text-gray-700">
                                    {safeConfig.quickAnswer.answer}
                                </p>
                                {safeConfig.quickAnswer.facts && safeConfig.quickAnswer.facts.length > 0 && (
                                    <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 border-t border-gray-100 pt-6">
                                        {safeConfig.quickAnswer.facts.map(fact => (
                                            <div key={fact.label} className="flex gap-2 text-sm">
                                                <dt className="font-bold text-gray-900 whitespace-nowrap">{fact.label}:</dt>
                                                <dd className="text-gray-600">{fact.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}
                            </>
                        )}
                        {safeConfig.explainer && (
                            <div className="mt-12">
                                <h2 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900 mb-4">
                                    {safeConfig.explainer.h2}
                                </h2>
                                {safeConfig.explainer.paragraphs.map((para, i) => (
                                    <p key={i} className="text-base leading-relaxed text-gray-700 mb-4">{para}</p>
                                ))}
                            </div>
                        )}
                        {safeConfig.lastUpdated && (
                            <p className="mt-8 text-xs text-gray-400">
                                Last updated: <time dateTime={safeConfig.lastUpdated}>{safeConfig.lastUpdated}</time>
                            </p>
                        )}
                    </div>
                </section>
            )}

            {/* Social proof ticker */}
            <section className="py-8 overflow-hidden border-y" style={{ backgroundColor: brand.primaryColor }}>
                <div className="max-w-7xl mx-auto px-6 flex items-center gap-8">
                    <div className="text-white text-xs font-bold uppercase tracking-widest whitespace-nowrap opacity-50">{t.socialProofLabel}</div>
                    <div className="flex-1 flex justify-between items-center gap-8 opacity-40 grayscale">
                        <span className="text-xl font-black text-white font-serif italic">Vanguard.</span>
                        <span className="text-xl font-bold text-white tracking-widest">NEXUS</span>
                        <span className="text-xl font-bold text-white font-mono">CODEBLOCK</span>
                        <span className="text-xl font-black text-white">turbo<span className="text-black">boost</span></span>
                        <span className="text-xl font-medium text-white">Aperture Sci</span>
                    </div>
                </div>
            </section>

            {/* === LAZY BLOCK 1: Problem + Features + How it Works + Testimonials === */}
            <React.Suspense fallback={<div style={{ minHeight: '800px' }} />}>
                <LazyBlockOne config={safeConfig} t={t} brand={brand} />
            </React.Suspense>

            {/* === LAZY BLOCK 2: Pricing + FAQ + CTA === */}
            <React.Suspense fallback={<div style={{ minHeight: '600px' }} />}>
                <LazyBlockTwo config={safeConfig} t={t} brand={brand} selectedTier={selectedTier} setSelectedTier={setSelectedTier} />
            </React.Suspense>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12 pb-12 border-b border-gray-800">
                        <div>
                            <div className="text-2xl font-black text-white tracking-tight mb-4">{brand.name.toUpperCase()}</div>
                            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">Premium {safeConfig.primaryKeyword} {t.footerDescription}</p>
                        </div>
                        <div>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">{t.footerRelatedServices}</h4>
                            <ul className="space-y-3 text-sm font-medium text-gray-300">
                                {safeConfig.relatedLinks.map(l => <li key={l.to}><Link to={l.to} className="hover:text-[#ff5a0a] transition-colors">{l.label}</Link></li>)}
                                <li><Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">{t.footerBuyTraffic}</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">{t.footerCompany}</h4>
                            <ul className="space-y-3 text-sm font-medium text-gray-300">
                                <li><Link to="/about" className="hover:text-[#ff5a0a] transition-colors">{t.footerAbout}</Link></li>
                                <li><Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">{t.footerBlog}</Link></li>
                                <li><Link to="/helpdesk" className="hover:text-[#ff5a0a] transition-colors">{t.footerHelpCenter}</Link></li>
                                <li><Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">{t.footerPrivacy}</Link></li>
                                <li><Link to="/legal/impressum" className="hover:text-[#ff5a0a] transition-colors">{t.footerImpressum}</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                        <p>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</p>
                        <div className="flex gap-2 items-center"><div className="w-2 h-2 rounded-full bg-green-500" /><span>{t.footerStatus}</span></div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

const TrafficLandingTemplate: React.FC<{ config: LandingPageConfig }> = ({ config }) => {
    const releaseMode = useUiReleaseMode();
    const location = useLocation();
    const { artifact, loading } = useLandingLocalizationArtifact(location.pathname);
    const localizedConfig = React.useMemo(
        () => materializeLandingLocalization(config, artifact),
        [artifact, config],
    );

    if (loading) {
        return <div data-landing-localization="loading" aria-busy="true" />;
    }

    if (shouldUseRedesignForSurface(releaseMode, 'landing-template')) {
        const landingLang = landingLanguageFromPath(location.pathname);
        const safeConfig = softenLandingConfig(localizedConfig, landingLang);
        const translations = softenTranslations({ ...DEFAULT_TRANSLATIONS, ...localizedConfig.translations }, landingLang);
        return <TrafficLandingV2 config={safeConfig} translations={translations} />;
    }

    return <TrafficLandingLegacy config={localizedConfig} />;
};

export default TrafficLandingTemplate;
